import React, { useState } from 'react';
import { Container, Form, Button, Card, Row, Col, Alert, Spinner, Table } from 'react-bootstrap';
import api from '../services/api';

function RateCalculator() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rate, setRate] = useState(null);
  const [formData, setFormData] = useState({
    fCity: '',
    fZip: '',
    tCity: '',
    tZip: '',
    ttw: '' // total weight
  });

  // Accessorial rates data
  const accessorialRates = [
    { service: 'Liftgate Pickup', rate: 75.00 },
    { service: 'Liftgate Delivery', rate: 75.00 },
    { service: 'Inside Pickup', rate: 85.00 },
    { service: 'Inside Delivery', rate: 85.00 },
    { service: 'Residential Pickup', rate: 75.00 },
    { service: 'Residential Delivery', rate: 75.00 },
    { service: 'Limited Access Pickup', rate: 75.00 },
    { service: 'Limited Access Delivery', rate: 75.00 },
    { service: 'Notification/Appointment', rate: 35.00 },
    { service: 'Sort and Segregate', rate: 1.50, unit: 'per piece' },
    { service: 'Detention', rate: 35.00, unit: 'per 15 minutes' },
    { service: 'Storage', rate: 25.00, unit: 'per day' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateRate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setRate(null);

    try {
      const response = await api.post('/api/bols/calculate-rate', {
        origin: {
          city: formData.fCity,
          zip: formData.fZip
        },
        destination: {
          city: formData.tCity,
          zip: formData.tZip
        },
        freightItems: [{
          pieces: 1,
          weight: formData.ttw
        }]
      });
      
      const data = response.data;
      
      if (data.success) {
        setRate(data);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.message || 'Failed to calculate rate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="p-4">
      <Row>
        <Col xs={12}>
          <Card className="mb-4">
            <Card.Header>
              <h6 className="card-title mb-0">
                Please Note: Rate Calculator is a rate lookup tool and not a quote for a specific shipment. 
                Your shipment may also be subject to accessorial and volumetric charges.
              </h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form onSubmit={calculateRate}>
                <Row>
                  <Col md={8} className="form-group mb-3">
                    <Form.Label htmlFor="originCity">Origin City</Form.Label>
                    <Form.Control
                      type="text"
                      id="originCity"
                      name="fCity"
                      value={formData.fCity}
                      onChange={handleInputChange}
                      placeholder="Enter origin city"
                      required
                    />
                  </Col>
                  <Col md={4} className="form-group mb-3">
                    <Form.Label htmlFor="originZip">Origin Zip</Form.Label>
                    <Form.Control
                      type="text"
                      id="originZip"
                      name="fZip"
                      value={formData.fZip}
                      onChange={handleInputChange}
                      placeholder="Enter origin zip"
                      required
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={8} className="form-group mb-3">
                    <Form.Label htmlFor="destinationCity">Destination City</Form.Label>
                    <Form.Control
                      type="text"
                      id="destinationCity"
                      name="tCity"
                      value={formData.tCity}
                      onChange={handleInputChange}
                      placeholder="Enter destination city"
                      required
                    />
                  </Col>
                  <Col md={4} className="form-group mb-3">
                    <Form.Label htmlFor="destinationZip">Destination Zip</Form.Label>
                    <Form.Control
                      type="text"
                      id="destinationZip"
                      name="tZip"
                      value={formData.tZip}
                      onChange={handleInputChange}
                      placeholder="Enter destination zip"
                      required
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={4} className="form-group mb-3">
                    <Form.Label htmlFor="totalWeight">Total Weight</Form.Label>
                    <Form.Control
                      type="number"
                      id="totalWeight"
                      name="ttw"
                      value={formData.ttw}
                      onChange={handleInputChange}
                      placeholder="Enter total weight"
                      required
                    />
                  </Col>
                </Row>
                    <Button type="submit" variant="primary" disabled={loading}>
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Calculating...
                        </>
                      ) : (
                        'Calculate Rate'
                      )}
                    </Button>
                  </Form>
                </Col>
                <Col md={6}>
                  {rate && (
                    <div className="mt-3">
                      <h6>Rate Calculation Results</h6>
                      <div className="border rounded p-3 bg-light">
                        <Row className="d-flex justify-content-between mb-2">
                          <Col xs="auto">Base Rate:</Col>
                          <Col xs="auto" className="text-end">
                            ${rate.rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Col>
                        </Row>
                        <Row className="d-flex justify-content-between mb-2">
                          <Col xs="auto">Fuel Surcharge:</Col>
                          <Col xs="auto" className="text-end">
                            ${rate.fsc.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Col>
                        </Row>
                        <hr className="my-2" />
                        <Row className="d-flex justify-content-between">
                          <Col xs="auto"><strong>Total:</strong></Col>
                          <Col xs="auto" className="text-end">
                            <strong>${rate.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  )}
                  {error && (
                    <Alert variant="danger" className="mt-3">
                      {error}
                    </Alert>
                  )}
                </Col>
              </Row>
            </Card.Body>

          </Card>

          {/* Accessorial Rates Section */}
          <Card>
            <Card.Header>
              <h6 className="card-title mb-0">Accessorial Rates</h6>
            </Card.Header>
            <Card.Body>
              <Row>
                {accessorialRates.map((item, index) => (
                  <Col md={6} lg={4} key={index} className="mb-3">
                    <div className="border rounded p-2 h-100 d-flex justify-content-between align-items-center">
                      <div className="fw-medium">{item.service}</div>
                      <div className="text-end text-primary fw-bold">
                        ${item.rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {item.unit && (
                          <div className="small text-muted">{item.unit}</div>
                        )}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
              <small className="text-muted">
                * Additional charges may apply based on specific requirements and circumstances.
                Please contact customer service for detailed information about accessorial services.
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default RateCalculator; 